import React, { useEffect, useMemo, useState } from 'react';
import { PrivateBasePage } from 'components/private-base-page.component';
import {
  APP_MANAGED_ROUTES,
  ERROR_TYPES,
  ErrorState,
  INELIGIBLE_CODES,
  LegalPage,
  LIVE_CHAT_DROP_ZONES,
  LiveChatLayoutContext,
  LoadingSpinner,
  MfaEnrollment,
  PageEligibilityContext,
  QUERY_PARAM_KEYS,
  RegionSelectionContext,
  useAnalyticsIdentify,
  usePrivateRoutesContainer,
  UserTypeContext,
} from '@highmark-web/highmark-common';
import { RewardsEventController } from '@leagueplatform/rewards-events';
import { useFeatureFlagQuery } from '@leagueplatform/user-profile-api';
import {
  ACHIEVEMENTS_ENABLED,
  REWARDS_EVENTS_ENABLED,
} from '@leagueplatform/rewards-api';
import { useConfigProperty } from '@leagueplatform/core';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import BoxoutIcon from 'assets/boxout.svg';
import { WalkthroughContainer } from './app-walkthrough/walkthrough-container.component';
import { useDynamicConfig } from '../config/use-dynamic-config.hook';
import { useDynamicEnGenConfig } from '../config/use-dynamic-engen-config.hook';

export const PrivateRoutesContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    contentConfig,
    eligibilityInfo,
    regionSelection,
    enableLiveChat,
    showWalkthrough,
    onCompleteWalkthrough,
    qualifiers,
    showMfa,
    onCompleteMfa,
    isLoading,
    errorAppInitDetails,
    errorUserProfile,
    ebillEligible,
    ebillRegistered,
    careManagementCohort,
    showCareManagementCohortModal,
    onCloseCareManagementCohortModal,
  } = usePrivateRoutesContainer();

  const [liveChatLayout, setLiveChatLayout] = useState(
    LIVE_CHAT_DROP_ZONES.BOTTOM_RIGHT,
  );
  const liveChatProviderValue = useMemo(
    () => ({ liveChatLayout, setLiveChatLayout }),
    [liveChatLayout, setLiveChatLayout],
  );

  const allMyHealthUrl = import.meta.env.VITE_AMH_APP_LOGIN_URL as string;

  const userTypeDetails = React.useMemo(
    () => ({
      isEasyPlanUser: qualifiers?.isEasyPlanUser || false,
    }),
    [qualifiers?.isEasyPlanUser],
  );

  // Dynamically update SDK config to load Firebase and API-dependent values
  useDynamicConfig(qualifiers?.childrensHealthInsuranceProgramUser);
  useDynamicEnGenConfig();

  const { data: isRewardsLayerEnabled } = useFeatureFlagQuery(
    REWARDS_EVENTS_ENABLED,
  );

  const { data: isAchievementsEnabled } =
    useFeatureFlagQuery(ACHIEVEMENTS_ENABLED);

  // Send user data to analytics SDK
  const tenantId = useConfigProperty('core.tenantId');
  useAnalyticsIdentify(tenantId!);

  // Redirect to care management cohort modal if eligible
  useEffect(() => {
    if (
      showCareManagementCohortModal &&
      careManagementCohort &&
      !location.search.includes(careManagementCohort)
    ) {
      navigate({
        pathname: location.pathname,
        search: `?${QUERY_PARAM_KEYS.modal}=${careManagementCohort}`,
      });
      onCloseCareManagementCohortModal();
    }
  }, [
    showCareManagementCohortModal,
    careManagementCohort,
    location.search,
    location.pathname,
    onCloseCareManagementCohortModal,
    navigate,
  ]);

  if (isLoading) return <LoadingSpinner />;

  if (errorAppInitDetails !== null) {
    return (
      <ErrorState
        error={errorAppInitDetails}
        isFullPage
        errorType={ERROR_TYPES.API_ERROR}
      />
    );
  }

  // Show not eligible screen if user isn't eligible to use the app
  if (!eligibilityInfo.eligible) {
    // Show specific not eligible screen if user hasn't had coverage for over 365 days
    switch (eligibilityInfo.reasonCode) {
      case INELIGIBLE_CODES.OVER_365_DAYS:
        return <ErrorState isFullPage errorType={ERROR_TYPES.OVER_365_DAYS} />;
      case 7:
        return (
          <ErrorState
            isFullPage
            imageSrc={BoxoutIcon}
            buttonLink={allMyHealthUrl}
            errorType={ERROR_TYPES.NOT_ELIGIBLE_ALLMYHEALTH}
          />
        );

      default:
        return <ErrorState isFullPage errorType={ERROR_TYPES.NOT_ELIGIBLE} />;
    }
  }

  // Show error screen if user is eligible, but userProfile API fails
  if (errorUserProfile !== null)
    return (
      <ErrorState
        error={errorUserProfile}
        isFullPage
        errorType={ERROR_TYPES.API_ERROR}
      />
    );

  // Show walkthrough slides if needed
  if (showWalkthrough) {
    return (
      <Routes>
        <Route path={`${APP_MANAGED_ROUTES.LEGAL}/*`} element={<LegalPage />} />
        <Route
          element={
            <WalkthroughContainer
              onCompleteWalkthrough={onCompleteWalkthrough}
            />
          }
          path="/*"
        />
      </Routes>
    );
  }

  if (showMfa) {
    return <MfaEnrollment onEnrollSuccess={onCompleteMfa} />;
  }

  return (
    <UserTypeContext.Provider value={userTypeDetails}>
      <RegionSelectionContext.Provider value={regionSelection}>
        <LiveChatLayoutContext.Provider value={liveChatProviderValue}>
          <PrivateBasePage
            enableLiveChat={enableLiveChat}
            enableEbillRegister={ebillEligible && !ebillRegistered}
          >
            {isRewardsLayerEnabled && isAchievementsEnabled && (
              <RewardsEventController />
            )}
            <PageEligibilityContext.Provider value={contentConfig}>
              {children}
            </PageEligibilityContext.Provider>
          </PrivateBasePage>
        </LiveChatLayoutContext.Provider>
      </RegionSelectionContext.Provider>
    </UserTypeContext.Provider>
  );
};
